import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adverseEventService } from '../services/adverseEventService';
import { useCallLogAccess } from './useCallLogAccess';

interface UsePatient360ModuleAvailabilityOptions {
  enabled?: boolean;
}

export type Patient360ModuleKey = 'ae-pc' | 'call-logs';

const MODULE_UNAVAILABLE_DEFAULTS: Record<Patient360ModuleKey, string> = {
  'ae-pc': 'AE / PC is unavailable until workflow setup is completed.',
  'call-logs': 'Call Logs is unavailable for this program or role.',
};

function buildUnavailableMessage(moduleKey: Patient360ModuleKey, reasons: string[]): string {
  const reason = reasons.find((r) => Boolean(r && r.trim()))?.trim();
  if (reason) {
    return /[.!?]$/.test(reason) ? reason : `${reason}.`;
  }
  return MODULE_UNAVAILABLE_DEFAULTS[moduleKey];
}

export interface Patient360ModuleAvailabilityState {
  isAvailable: boolean;
  isLoading: boolean;
  reasons: string[];
  unavailableMessage: string;
}

export type Patient360ModuleAvailabilityMap = Record<Patient360ModuleKey, Patient360ModuleAvailabilityState>;

/**
 * Centralized setup availability checks for Patient360 modules.
 *
 * AE / PC availability is determined from workflow readiness so we can:
 * - hide unavailable submenu entries before render (no flicker), and
 * - guard direct route access when setup is incomplete.
 */
export function usePatient360ModuleAvailability(
  programId?: string,
  options?: UsePatient360ModuleAvailabilityOptions,
) {
  const callLogAccess = useCallLogAccess(programId);

  const query = useQuery({
    queryKey: ['patient360', 'module-availability', 'ae-pc', programId],
    queryFn: () => adverseEventService.getWorkflowStatus(programId!),
    enabled: Boolean(programId) && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  });

  const isAePcAvailable = useMemo(() => {
    const status = query.data;
    if (!status) return false;
    return Boolean(status.isConfigured && status.hasActiveWorkflow && status.hasInitialStep);
  }, [query.data]);

  const modules = useMemo<Patient360ModuleAvailabilityMap>(() => {
    const aePcReasons = query.data?.missingRequirements ?? [];
    const callLogsReasons: string[] = [];

    if (!callLogAccess.isLoading && !callLogAccess.canView) {
      if (callLogAccess.config && callLogAccess.config.enabled === false) {
        callLogsReasons.push('Call Logs feature is disabled for this program.');
      } else {
        callLogsReasons.push('Call Logs access is not granted for this role.');
      }
    }

    return {
      'ae-pc': {
        isAvailable: isAePcAvailable,
        isLoading: query.isLoading,
        reasons: aePcReasons,
        unavailableMessage: buildUnavailableMessage('ae-pc', aePcReasons),
      },
      'call-logs': {
        isAvailable: callLogAccess.canView,
        isLoading: callLogAccess.isLoading,
        reasons: callLogsReasons,
        unavailableMessage: buildUnavailableMessage('call-logs', callLogsReasons),
      },
    };
  }, [
    isAePcAvailable,
    query.isLoading,
    query.data?.missingRequirements,
    callLogAccess.canView,
    callLogAccess.isLoading,
    callLogAccess.config,
  ]);

  return {
    modules,
    isAePcAvailable,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    workflowStatus: query.data ?? null,
  };
}
